import type { LucideIcon } from "lucide-react";
import {
  Database,
  Gem,
  Grid3x3,
  LayoutDashboard,
  Layers,
  Monitor,
  Navigation,
  Package,
  Paintbrush,
  Palette,
  Sparkles,
  Trophy,
  Type,
} from "lucide-react";

export type PillarCode = "LS" | "LM" | "LC" | "LD";

export interface SigilPillarLink {
  readonly to: string;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly isMvp?: boolean;
  /**
   * True when this pillar is the canonical owner of the route. Routes that
   * appear in more than one pillar (e.g. `/colors` is shared by LS + LD) set
   * `isPrimary` only on the owner so navigation surfaces can dedupe without
   * losing the secondary pillar's overview-hub chip.
   */
  readonly isPrimary?: boolean;
  /**
   * True when the route source still lives under `src/_archive_v0/` and is
   * not yet wired into the TanStack route tree. Surfaces should render the
   * link but treat it as a soft "coming in v1.x" entry.
   */
  readonly isArchived?: boolean;
}

export interface SigilPillar {
  readonly code: PillarCode;
  readonly publicName: string;
  readonly packageLabel: string;
  readonly description: string;
  readonly icon: LucideIcon;
  readonly links: readonly SigilPillarLink[];
}

/** Canonical 4-SIGILS map — Bible v1.1 / rebuild strategy §3 + §4. */
export const sigilPillars: readonly SigilPillar[] = [
  {
    code: "LS",
    publicName: "Legendary Style",
    packageLabel: "@engagexp/legendary-css",
    description:
      "Color, typography, tokens, grid, and responsive foundations—the CSS and token spine every app consumes.",
    icon: Palette,
    links: [
      { to: "/colors", label: "Colors", icon: Palette, isMvp: true, isPrimary: true },
      { to: "/typography", label: "Typography", icon: Type, isMvp: true, isPrimary: true },
      { to: "/tokens", label: "Tokens", icon: Database, isMvp: true, isPrimary: true, isArchived: true },
      { to: "/grid", label: "Grid", icon: Grid3x3, isPrimary: true, isArchived: true },
      { to: "/responsive", label: "Responsive", icon: Monitor, isPrimary: true, isArchived: true },
    ],
  },
  {
    code: "LM",
    publicName: "Legendary Motion",
    packageLabel: "@engagexp/legendary-ux",
    description:
      "Motion presets, navigation choreography, dashboards, and gamified flows—with reduced-motion parity.",
    icon: Sparkles,
    links: [
      { to: "/motion", label: "Motion", icon: Sparkles, isMvp: true, isPrimary: true, isArchived: true },
      { to: "/navigation", label: "Navigation", icon: Navigation, isPrimary: true, isArchived: true },
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, isPrimary: true, isArchived: true },
      { to: "/gamification", label: "Gamification", icon: Trophy, isPrimary: true, isArchived: true },
    ],
  },
  {
    code: "LC",
    publicName: "Legendary Components",
    packageLabel: "@engagexp/legendary-ui",
    description:
      "The Relume-aligned component catalog and product surfaces—primitives through composed patterns.",
    icon: Layers,
    links: [
      { to: "/components", label: "Components", icon: Layers, isMvp: true, isPrimary: true, isArchived: true },
      { to: "/product/exp", label: "Product showcase", icon: Package, isPrimary: true, isArchived: true },
    ],
  },
  {
    code: "LD",
    publicName: "Legendary Design",
    packageLabel: "@engagexp/legendary-design",
    description:
      "Brand identity, tier system, and design-kit alignment—shared color surfaces with Legendary Style.",
    icon: Gem,
    links: [
      { to: "/brand", label: "Brand", icon: Paintbrush, isMvp: true, isPrimary: true },
      { to: "/colors", label: "Colors (shared)", icon: Palette, isMvp: true },
    ],
  },
];
